'use client'

/**
 * BuyCreditsDialog — Anthropic-style "Add credits" modal.
 *
 * Fields:
 *   - Credits ($5 – $200,000)
 *   - Auto-reload toggle (toggleable inline, OFF if not yet set up)
 *     - When ON: threshold + top-up target inputs (mirrors AutoReloadDialog)
 *   - Subtotal / Estimated taxes / Total
 *   - "Buy $X of credits" CTA (Stripe Checkout)
 *   - Fine-print legal
 *
 * v1 note: taxes always show $0.00 because we don't run Stripe Tax — the row
 * is here for design parity and so we have a slot to drop real values into
 * later. Buy CTA hits the existing /api/wallet/deposit which redirects to
 * Stripe Checkout.
 *
 * Stephen 2026-05-22 — credit balance pivot.
 */

import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { CurrencyInput } from './CurrencyInput'

interface PaymentMethod {
  id: string
  brand: string
  last4: string
}

interface AutoReloadState {
  enabled: boolean
  threshold_cents: number
  amount_cents: number
  has_payment_method: boolean
  payment_method: PaymentMethod | null
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  autoReload: AutoReloadState | null
  onPurchased: () => void | Promise<void>
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function formatCurrencyShort(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })
}

const MIN_PURCHASE_CENTS = 500       // $5
const MAX_PURCHASE_CENTS = 20_000_000 // $200,000
const DEFAULT_PURCHASE_CENTS = 25_000 // $250

// ── Component ────────────────────────────────────────────────────────────────

export function BuyCreditsDialog({ open, onOpenChange, autoReload, onPurchased }: Props) {
  const [amountCents, setAmountCents] = useState(DEFAULT_PURCHASE_CENTS)

  // Mirror the existing auto-reload state if user opens dialog with it ON.
  const [autoReloadEnabled, setAutoReloadEnabled] = useState(false)
  const [thresholdCents, setThresholdCents] = useState(500)
  const [topupCents, setTopupCents] = useState(10000)

  const [submitting, setSubmitting] = useState(false)

  // Reset form when dialog opens.
  useEffect(() => {
    if (!open) return
    setAmountCents(DEFAULT_PURCHASE_CENTS)
    if (autoReload?.enabled) {
      setAutoReloadEnabled(true)
      setThresholdCents(autoReload.threshold_cents)
      setTopupCents(autoReload.amount_cents)
    } else {
      setAutoReloadEnabled(false)
      setThresholdCents(500)
      setTopupCents(10000)
    }
  }, [open, autoReload])

  const amountError = useMemo(() => {
    if (!Number.isFinite(amountCents) || amountCents < MIN_PURCHASE_CENTS) {
      return `Enter an amount between ${formatCurrencyShort(MIN_PURCHASE_CENTS)} and ${formatCurrencyShort(MAX_PURCHASE_CENTS)}`
    }
    if (amountCents > MAX_PURCHASE_CENTS) {
      return `Enter an amount between ${formatCurrencyShort(MIN_PURCHASE_CENTS)} and ${formatCurrencyShort(MAX_PURCHASE_CENTS)}`
    }
    return null
  }, [amountCents])

  const arErrors = useMemo(() => {
    if (!autoReloadEnabled) return {}
    const e: { threshold?: string; topup?: string } = {}
    if (thresholdCents < 500) e.threshold = 'Minimum $5'
    if (topupCents < 1000) e.topup = 'Minimum $10'
    if (topupCents <= thresholdCents) e.topup = 'Top-up amount must be greater than the threshold'
    return e
  }, [autoReloadEnabled, thresholdCents, topupCents])

  const totalErrors = amountError || Object.keys(arErrors).length > 0

  const handleBuy = async () => {
    if (totalErrors) return
    setSubmitting(true)
    try {
      // Kick off the deposit checkout. Auto-reload prefs are passed along
      // so they get applied AFTER the deposit succeeds (in the Stripe webhook),
      // not before. This prevents users from accidentally enabling auto-fund
      // by cancelling the checkout (audit finding #10).
      const res = await fetch('/api/wallet/deposit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount_cents: amountCents,
          auto_reload: autoReloadEnabled
            ? { enabled: true, threshold_cents: thresholdCents, amount_cents: topupCents }
            : null,
        }),
      })
      const data = await res.json() as { checkout_url?: string; error?: string }
      if (!res.ok || !data.checkout_url) {
        throw new Error(data.error || 'Failed to start checkout')
      }
      // Redirect — onPurchased fires when user lands back on /budget?deposit=success
      void onPurchased()
      window.location.href = data.checkout_url
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to start checkout')
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" showCloseButton>
        <DialogHeader>
          <DialogTitle className="font-sans text-xl font-medium tracking-[-0.3px]">Add credits</DialogTitle>
          <DialogDescription>
            Credits are charged to your default payment method and applied immediately.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5 py-2">
          {/* Amount */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between">
              <Label htmlFor="bc-amount">Credits</Label>
              <span className="text-xs text-atrium-muted">
                Enter an amount between {formatCurrencyShort(MIN_PURCHASE_CENTS)} and {formatCurrencyShort(MAX_PURCHASE_CENTS)}
              </span>
            </div>
            <CurrencyInput
              id="bc-amount"
              valueCents={amountCents}
              onChangeCents={setAmountCents}
              minCents={MIN_PURCHASE_CENTS}
              maxCents={MAX_PURCHASE_CENTS}
              disabled={submitting}
              error={amountError ?? undefined}
            />
            {amountError && (
              <p className="text-xs text-red-600">{amountError}</p>
            )}
          </div>

          {/* Auto-reload toggle */}
          <div className="rounded-xl border border-atrium-border bg-white p-4 dark:bg-atrium-inner">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h4 className="font-sans text-base font-medium text-atrium-black">Auto-reload credits</h4>
                <p className="mt-1 text-xs text-atrium-muted">
                  Avoid service disruptions by auto-reloading credits when your balance reaches a specified minimum.
                </p>
              </div>
              <Switch
                checked={autoReloadEnabled}
                onCheckedChange={setAutoReloadEnabled}
                disabled={submitting || !autoReload?.has_payment_method}
                aria-label="Toggle auto-reload"
              />
            </div>

            {autoReloadEnabled && (
              <div className="mt-4 grid grid-cols-1 items-end gap-3 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="bc-ar-threshold">When credit balance reaches:</Label>
                  <CurrencyInput
                    id="bc-ar-threshold"
                    valueCents={thresholdCents}
                    onChangeCents={setThresholdCents}
                    minCents={500}
                    disabled={submitting}
                    error={arErrors.threshold}
                  />
                  {arErrors.threshold && (
                    <p className="text-xs text-red-600">{arErrors.threshold}</p>
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="bc-ar-topup">Bring balance back up to:</Label>
                  <CurrencyInput
                    id="bc-ar-topup"
                    valueCents={topupCents}
                    onChangeCents={setTopupCents}
                    minCents={1000}
                    disabled={submitting}
                    error={arErrors.topup}
                  />
                  {arErrors.topup && (
                    <p className="text-xs text-red-600">{arErrors.topup}</p>
                  )}
                </div>
              </div>
            )}

            {!autoReload?.has_payment_method && (
              <p className="mt-3 text-xs text-atrium-muted">
                Add a payment method first to enable auto-reload.
              </p>
            )}
          </div>

          {/* Order summary */}
          <div className="rounded-xl border border-atrium-border bg-atrium-inner/40 p-4">
            <dl className="space-y-1.5 text-sm">
              <div className="flex justify-between text-atrium-muted">
                <dt>Subtotal</dt>
                <dd className="font-mono tabular-nums text-atrium-black">{formatCurrency(amountCents)}</dd>
              </div>
              <div className="flex justify-between text-atrium-muted">
                <dt>Estimated taxes</dt>
                <dd className="font-mono tabular-nums text-atrium-black">{formatCurrency(0)}</dd>
              </div>
              <div className="flex justify-between font-medium border-t border-atrium-border pt-1.5 mt-1.5">
                <dt className="text-atrium-black">Total</dt>
                <dd className="font-mono tabular-nums text-atrium-black">{formatCurrency(amountCents)}</dd>
              </div>
            </dl>
          </div>

          {/* CTA */}
          <Button
            type="button"
            onClick={handleBuy}
            disabled={submitting || !!totalErrors}
            className="w-full justify-center"
            size="lg"
          >
            {submitting ? 'Starting checkout…' : `Buy ${formatCurrency(amountCents)} of credits`}
          </Button>

          <p className="text-[11px] leading-relaxed text-atrium-muted">
            Credits are consumed as you run campaigns. They never expire on Atrium today, but
            unused credits remain refundable per our{' '}
            <a href="https://www.atrium.run/privacy" target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-atrium-black">
              Terms
            </a>
            . You&rsquo;ll be redirected to Stripe to complete your purchase.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
